import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Factory, Clock, Shield, TrendingDown, CheckCircle2, Building2 } from 'lucide-react';

const ConstructionTech = () => {
  const products = [
    {
      title: 'Precast Columns & Beams',
      image: 'https://images.pexels.com/photos/21912668/pexels-photo-21912668.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      specs: [
        'M25-M40 structural grade',
        '6-12m height capability',
        'IS 13920 seismic compliant',
        '±2mm precision tolerance'
      ]
    },
    {
      title: 'Hollow Core Slabs (HCS)',
      image: 'https://images.unsplash.com/photo-13261149/pexels-photo-13261149.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
      specs: [
        'Rapid floor construction',
        '6-12m span capacity',
        'Pre-stressed design',
        'Fire & sound resistant'
      ]
    },
    {
      title: 'Double-T Slabs',
      image: 'https://images.unsplash.com/photo-1581094271901-8022df4466f9?q=80&w=2070',
      specs: [
        '15-20m clear span',
        'Column-free spaces',
        'Ideal for warehouses',
        'Heavy load capacity'
      ]
    },
    {
      title: 'Architectural Wall Panels',
      image: 'https://static.prod-images.emergentagent.com/jobs/c1621811-536c-492b-92e2-9a5fac8307e3/images/7f12a5791c00dbd1a124f82a1f6b5c2622ba9c5d63a08cb272905b9f24e9c5f5.png',
      specs: [
        'Façade engineering',
        'Premium finishes',
        'Thermal insulation',
        'Weather resistant'
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20 lg:pt-24">
      <Helmet>
        <title>Precast Concrete Construction Services | Arthaa Build — 50% Faster Delivery</title>
        <meta name="description" content="Arthaa Build precast concrete construction services — columns, beams, hollow core slabs, double-T slabs, wall panels, boundary walls. IS 13920 seismic certified. Factory-manufactured in Chandigarh for Punjab, Haryana, Delhi NCR." />
        <link rel="canonical" href="https://arthaabuild.com/construction" />
      </Helmet>

      {/* Header */}
      <section className="section-padding bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <p className="text-sm uppercase tracking-widest text-[#7EB3D8] font-semibold mb-4">Arthaa Build Construction Technology</p>
            <h1 className="font-serif font-bold text-5xl sm:text-6xl mb-6">
              Advanced Precast Concrete Solutions
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              Arthaa Build delivers factory-manufactured precision with on-site speed. Infrastructure, residential, and commercial projects — 50% faster with 20% overall cost savings.
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-[#7EB3D8] mb-1">60-75 Days</div>
                <div className="text-xs text-white/80">vs 120+ Traditional</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-[#7EB3D8] mb-1">20% Less</div>
                <div className="text-xs text-white/80">Cost Savings</div>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-[#7EB3D8] mb-1">IS 13920</div>
                <div className="text-xs text-white/80">Certified</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <div className="text-center mb-16">
            <h2 className="font-serif font-bold text-4xl text-slate-900 mb-4">Our Precast Solutions</h2>
            <p className="text-lg text-slate-600">Comprehensive range of factory-manufactured components</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {products.map((product, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="luxury-card bg-white rounded-xl overflow-hidden"
              >
                <div className="h-64">
                  <img src={product.image} alt={product.title} className="w-full h-full object-cover" />
                </div>
                <div className="p-8">
                  <h3 className="font-serif font-bold text-2xl text-slate-900 mb-4">{product.title}</h3>
                  <ul className="space-y-2 mb-6">
                    {product.specs.map((spec, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-[#2B5278] flex-shrink-0" />
                        <span>{spec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="section-padding bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-20">
          <h2 className="font-serif font-bold text-4xl text-slate-900 mb-12 text-center">Complete Construction Services</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="luxury-card bg-white p-8 rounded-xl">
              <Building2 className="w-12 h-12 text-[#2B5278] mb-4" />
              <h3 className="font-serif font-bold text-xl text-slate-900 mb-3">Façade Engineering</h3>
              <p className="text-slate-600 text-sm">Modern exterior cladding systems with thermal and acoustic insulation.</p>
            </div>
            <div className="luxury-card bg-white p-8 rounded-xl">
              <Factory className="w-12 h-12 text-[#2B5278] mb-4" />
              <h3 className="font-serif font-bold text-xl text-slate-900 mb-3">MEP Solutions</h3>
              <p className="text-slate-600 text-sm">Integrated mechanical, electrical, and plumbing systems design and execution.</p>
            </div>
            <div className="luxury-card bg-white p-8 rounded-xl">
              <Shield className="w-12 h-12 text-[#2B5278] mb-4" />
              <h3 className="font-serif font-bold text-xl text-slate-900 mb-3">High-Quality Finishing</h3>
              <p className="text-slate-600 text-sm">Premium interior and exterior finishing works for turnkey project delivery.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-r from-[#2B5278] to-[#1E3A5F] text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-20 text-center">
          <h2 className="font-serif font-bold text-4xl mb-6">Ready to Build Faster & Save Costs?</h2>
          <p className="text-xl text-white/90 mb-8">Get a detailed feasibility study and cost comparison for your project.</p>
          <Link to="/contact" className="inline-block bg-white text-slate-900 hover:bg-slate-50 font-semibold px-10 py-4 rounded-md transition-all">
            Request Project Analysis
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ConstructionTech;